import React, { useState, useEffect } from 'react';
import { useAdvertisementDisplaySettingsServices } from '../../apis/advertisementDisplaySettingsService';
import { useNewsServices } from '../../apis/newsService';
import { useBlogServices } from '../../apis/blogService';
import { Save, Megaphone, FileText, AlertCircle, CheckCircle, Info, Calculator } from 'lucide-react';
import { toast } from 'react-toastify';

const AdvertisementDisplaySettingsPage = () => {
  const [settings, setSettings] = useState({
    enabledForNews: false,
    enabledForBlog: false,
    newsAdFrequency: 10,
    blogAdFrequency: 10,
    useFeaturedAds: true,
    adRotation: 'sequential'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newsCount, setNewsCount] = useState(0);
  const [blogCount, setBlogCount] = useState(0);
  const [desiredNewsAds, setDesiredNewsAds] = useState(5);
  const [desiredBlogAds, setDesiredBlogAds] = useState(5);
  
  const { getAdvertisementDisplaySettings, updateAdvertisementDisplaySettings, loading, error } = useAdvertisementDisplaySettingsServices();
  const { getPublicNews } = useNewsServices();
  const { getPublicBlogs } = useBlogServices();

  // Fetch settings and counts on component mount
  useEffect(() => {
    fetchSettings();
    fetchCounts();
  }, []);

  // Show error if API call fails
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await getAdvertisementDisplaySettings();
      if (response.success && response.data) {
        setSettings({
          enabledForNews: response.data.enabledForNews ?? false,
          enabledForBlog: response.data.enabledForBlog ?? false,
          newsAdFrequency: response.data.newsAdFrequency ?? 10,
          blogAdFrequency: response.data.blogAdFrequency ?? 10,
          useFeaturedAds: response.data.useFeaturedAds ?? true,
          adRotation: response.data.adRotation ?? 'sequential'
        });
      } else {
        toast.error(response.message || 'Failed to fetch advertisement display settings');
      }
    } catch (err) {
      console.error('Error fetching advertisement display settings:', err);
      toast.error('Failed to load advertisement display settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      // Get total count of active news
      const newsResponse = await getPublicNews({ limit: 1, page: 1 });
      if (newsResponse.success) {
        setNewsCount(newsResponse.data.pagination?.total || 0);
      }

      // Get total count of active blogs
      const blogResponse = await getPublicBlogs({ limit: 1, page: 1 });
      if (blogResponse.success) {
        setBlogCount(blogResponse.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const handleToggle = (key) => {
    setSettings({
      ...settings,
      [key]: !settings[key]
    });
  };

  const handleInputChange = (key, value) => {
    setSettings({
      ...settings,
      [key]: value
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await updateAdvertisementDisplaySettings(settings);
      if (response.success) {
        toast.success('Advertisement display settings updated successfully');
        setSettings({
          enabledForNews: response.data.enabledForNews,
          enabledForBlog: response.data.enabledForBlog,
          newsAdFrequency: response.data.newsAdFrequency,
          blogAdFrequency: response.data.blogAdFrequency,
          useFeaturedAds: response.data.useFeaturedAds,
          adRotation: response.data.adRotation
        });
        // Refresh counts
        fetchCounts();
      } else {
        toast.error(response.message || 'Failed to update advertisement display settings');
      }
    } catch (err) {
      console.error('Error updating advertisement display settings:', err);
      toast.error('Failed to update advertisement display settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate how many ads will show
  const calculateAdsCount = (totalCount, frequency) => {
    if (totalCount === 0 || frequency <= 0) return 0;
    return Math.floor(totalCount / frequency);
  };

  // Calculate how many articles needed for desired ads
  const calculateArticlesNeeded = (desiredAds, frequency) => {
    if (desiredAds <= 0 || frequency <= 0) return 0;
    return desiredAds * frequency;
  };

  const newsAdsCount = calculateAdsCount(newsCount, settings.newsAdFrequency);
  const blogAdsCount = calculateAdsCount(blogCount, settings.blogAdFrequency);
  const newsArticlesNeeded = calculateArticlesNeeded(desiredNewsAds, settings.newsAdFrequency);
  const blogArticlesNeeded = calculateArticlesNeeded(desiredBlogAds, settings.blogAdFrequency);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading advertisement display settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Advertisement Display Settings</h1>
          <p className="text-gray-500 mt-1">Control how advertisements appear between news and blog posts</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <Save size={18} className="mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
        <Info className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">How it works:</p>
          <p>Set the frequency to control how often ads appear. For example, a frequency of 10 means 1 advertisement will appear after every 10 articles. The system automatically calculates how many ads will show based on your total content.</p>
        </div>
      </div>

      {/* News Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-3">
              <FileText className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">News Articles</h3>
              <p className="text-sm text-gray-500">Show advertisements between news articles</p>
            </div>
          </div>
          <div className={`flex items-center justify-center w-14 h-7 rounded-full transition-colors ${
            settings.enabledForNews ? 'bg-green-500' : 'bg-gray-300'
          }`}>
            <button
              onClick={() => handleToggle('enabledForNews')}
              className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                settings.enabledForNews ? 'translate-x-3.5' : 'translate-x-0.5'
              }`}
            />
          </div>
        </div>

        {settings.enabledForNews && (
          <div className="space-y-4 pl-16">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Advertisement Frequency
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={settings.newsAdFrequency}
                  onChange={(e) => handleInputChange('newsAdFrequency', parseInt(e.target.value) || 1)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">
                  Show 1 advertisement per {settings.newsAdFrequency} news article{settings.newsAdFrequency !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Preview Calculation */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <Calculator className="text-gray-600 mr-2" size={18} />
                <span className="text-sm font-medium text-gray-700">Current Preview</span>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Total active news articles: <span className="font-semibold">{newsCount}</span></p>
                <p>With frequency of {settings.newsAdFrequency}, <span className="font-semibold text-blue-600">{newsAdsCount} advertisement{newsAdsCount !== 1 ? 's' : ''}</span> will be displayed</p>
              </div>
            </div>

            {/* Desired Ads Calculator */}
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center mb-2">
                <Calculator className="text-yellow-600 mr-2" size={18} />
                <span className="text-sm font-medium text-yellow-800">Calculate Articles Needed</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">I want</label>
                  <input
                    type="number"
                    min="1"
                    value={desiredNewsAds}
                    onChange={(e) => setDesiredNewsAds(parseInt(e.target.value) || 1)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <label className="text-sm text-gray-700">advertisement{desiredNewsAds !== 1 ? 's' : ''}</label>
                </div>
                <span className="text-sm text-gray-600">→</span>
                <div className="text-sm">
                  <span className="text-gray-600">Need </span>
                  <span className="font-semibold text-yellow-700">{newsArticlesNeeded} news article{newsArticlesNeeded !== 1 ? 's' : ''}</span>
                  {newsArticlesNeeded > newsCount && (
                    <span className="text-red-600 ml-2">(Currently have {newsCount})</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Blog Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg mr-3">
              <FileText className="text-purple-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Blog Posts</h3>
              <p className="text-sm text-gray-500">Show advertisements between blog posts</p>
            </div>
          </div>
          <div className={`flex items-center justify-center w-14 h-7 rounded-full transition-colors ${
            settings.enabledForBlog ? 'bg-green-500' : 'bg-gray-300'
          }`}>
            <button
              onClick={() => handleToggle('enabledForBlog')}
              className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                settings.enabledForBlog ? 'translate-x-3.5' : 'translate-x-0.5'
              }`}
            />
          </div>
        </div>

        {settings.enabledForBlog && (
          <div className="space-y-4 pl-16">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Advertisement Frequency
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={settings.blogAdFrequency}
                  onChange={(e) => handleInputChange('blogAdFrequency', parseInt(e.target.value) || 1)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">
                  Show 1 advertisement per {settings.blogAdFrequency} blog post{settings.blogAdFrequency !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Preview Calculation */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <Calculator className="text-gray-600 mr-2" size={18} />
                <span className="text-sm font-medium text-gray-700">Current Preview</span>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Total active blog posts: <span className="font-semibold">{blogCount}</span></p>
                <p>With frequency of {settings.blogAdFrequency}, <span className="font-semibold text-purple-600">{blogAdsCount} advertisement{blogAdsCount !== 1 ? 's' : ''}</span> will be displayed</p>
              </div>
            </div>

            {/* Desired Ads Calculator */}
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center mb-2">
                <Calculator className="text-yellow-600 mr-2" size={18} />
                <span className="text-sm font-medium text-yellow-800">Calculate Articles Needed</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">I want</label>
                  <input
                    type="number"
                    min="1"
                    value={desiredBlogAds}
                    onChange={(e) => setDesiredBlogAds(parseInt(e.target.value) || 1)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <label className="text-sm text-gray-700">advertisement{desiredBlogAds !== 1 ? 's' : ''}</label>
                </div>
                <span className="text-sm text-gray-600">→</span>
                <div className="text-sm">
                  <span className="text-gray-600">Need </span>
                  <span className="font-semibold text-yellow-700">{blogArticlesNeeded} blog post{blogArticlesNeeded !== 1 ? 's' : ''}</span>
                  {blogArticlesNeeded > blogCount && (
                    <span className="text-red-600 ml-2">(Currently have {blogCount})</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advanced Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Advanced Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Use Only Featured Advertisements</label>
              <p className="text-xs text-gray-500 mt-1">When enabled, only featured ads will be shown. When disabled, all active ads will be used.</p>
            </div>
            <div className={`flex items-center justify-center w-14 h-7 rounded-full transition-colors ${
              settings.useFeaturedAds ? 'bg-green-500' : 'bg-gray-300'
            }`}>
              <button
                onClick={() => handleToggle('useFeaturedAds')}
                className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  settings.useFeaturedAds ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Advertisement Rotation
            </label>
            <select
              value={settings.adRotation}
              onChange={(e) => handleInputChange('adRotation', e.target.value)}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="sequential">Sequential (in order)</option>
              <option value="random">Random</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Sequential shows ads in order, Random shuffles them each time.</p>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Settings Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">News Advertisements:</p>
            <p className={`font-medium ${settings.enabledForNews ? 'text-green-600' : 'text-gray-400'}`}>
              {settings.enabledForNews ? `Enabled - 1 ad per ${settings.newsAdFrequency} news (${newsAdsCount} ads will show)` : 'Disabled'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Blog Advertisements:</p>
            <p className={`font-medium ${settings.enabledForBlog ? 'text-green-600' : 'text-gray-400'}`}>
              {settings.enabledForBlog ? `Enabled - 1 ad per ${settings.blogAdFrequency} blogs (${blogAdsCount} ads will show)` : 'Disabled'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Ad Source:</p>
            <p className="font-medium text-gray-800">
              {settings.useFeaturedAds ? 'Featured Advertisements Only' : 'All Active Advertisements'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Rotation:</p>
            <p className="font-medium text-gray-800 capitalize">
              {settings.adRotation}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvertisementDisplaySettingsPage;



